/**
 * Modal Gift Card - Gift card balance verification
 * Handles form validation, API calls, and UI state management
 */

class ModalGiftCard {
  constructor(config) {
    this.sectionId = config.sectionId;
    this.testMode = config.testMode;
    this.makeWebhookUrl = config.makeWebhookUrl;
    this.texts = config.texts;
    this.labels = config.labels;
    
    this.init();
  }

  init() {
    this.modal = document.querySelector(`[data-section-id="${this.sectionId}"]`);
    if (!this.modal) {
      console.error('Modal element not found');
      return;
    }

    // Any link/element that should open this modal.
    // Robust to multiple triggers, dynamically injected content and
    // Shopify url settings that may alter the stored value.
    this.triggerSelector = 'a[href$="#open-modal-gift"], [data-open-gift-modal]';
    this.lastTrigger = null;

    this.cacheElements();
    this.bindEvents();
  }

  cacheElements() {
    this.overlay = this.modal.querySelector('.js-modal-gift-overlay');
    this.closeBtn = this.modal.querySelector('.js-modal-gift-close');
    this.form = this.modal.querySelector('.js-gift-card-form');
    this.input = this.modal.querySelector('.js-gift-card-number');
    this.submitBtn = this.modal.querySelector('.js-submit-btn');
    this.balanceInfo = this.modal.querySelector('.js-balance-info');
    this.balanceValue = this.modal.querySelector('.js-balance-value');
    this.balanceStatus = this.modal.querySelector('.js-balance-status');
    this.formMessage = this.modal.querySelector('.js-form-message');
    this.expirationValueEl = this.modal.querySelector('.js-expiration-value');
  }

  bindEvents() {
    // Open modal via event delegation so it works for any number of
    // triggers, regardless of when they are added to the DOM.
    document.addEventListener('click', (e) => {
      const trigger = e.target.closest(this.triggerSelector);
      if (!trigger) return;
      e.preventDefault();
      this.lastTrigger = trigger;
      this.openModal();
    });

    // Close modal
    this.closeBtn.addEventListener('click', () => this.closeModal());
    this.overlay.addEventListener('click', () => this.closeModal());

    // Close on ESC
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.modal.classList.contains('is-open')) {
        this.closeModal();
      }
    });

    // Form submission
    this.form.addEventListener('submit', (e) => this.handleSubmit(e));

    // Re-enable submit button whenever the gift card number changes,
    // so the user can run a new balance check.
    this.input.addEventListener('input', () => {
      this.submitBtn.disabled = false;
    });
  }

  openModal() {
    this.modal.classList.add('is-open');
    document.body.style.overflow = 'hidden';
    this.resetForm();
    // Focus on title
    const modalTitle = this.modal.querySelector('#modal-gift-card-title');
    setTimeout(() => modalTitle.focus(), 100);
  }

  closeModal() {
    this.modal.classList.remove('is-open');
    document.body.style.overflow = '';
    // Return focus to the trigger that opened the modal
    if (this.lastTrigger) {
      this.lastTrigger.focus();
    }
  }

  validateForm() {
    let isValid = true;
    const numberError = this.modal.querySelector('.js-gift-card-number-error');

    // Reset errors
    numberError.textContent = '';
    numberError.classList.remove('show');
    this.input.classList.remove('error');
    this.input.setAttribute('aria-invalid', 'false');

    // Validate number
    if (!this.input.value.trim()) {
      numberError.textContent = this.texts.numberError;
      numberError.classList.add('show');
      this.input.classList.add('error');
      this.input.setAttribute('aria-invalid', 'true');
      isValid = false;
    } else if (this.input.value.trim().length < 8) {
      numberError.textContent = this.texts.lengthError;
      numberError.classList.add('show');
      this.input.classList.add('error');
      this.input.setAttribute('aria-invalid', 'true');
      isValid = false;
    }

    return isValid;
  }

  resetForm() {
    this.form.reset();
    this.clearResults();
    this.modal.querySelector('.js-gift-card-number-error').classList.remove('show');
    this.input.classList.remove('error');
  }

  clearResults() {
    this.balanceInfo.style.display = 'none';
    this.balanceValue.textContent = '';
    this.expirationValueEl.textContent = '-';
    this.balanceStatus.textContent = '';
    this.balanceStatus.classList.remove('active', 'expired', 'zero-balance');
    this.balanceInfo.classList.remove('active', 'expired', 'zero-balance');
    this.formMessage.style.display = 'none';
    this.formMessage.textContent = '';
    this.formMessage.classList.remove('success', 'error', 'info');
  }

  async fetchGiftCardData(giftCardNumber) {
    if (this.testMode) {
      return this.getTestData(giftCardNumber);
    } else {
      return this.fetchFromAPI(giftCardNumber);
    }
  }

  getTestData(giftCardNumber) {
    let mockBalance = 0;
    let daysUntilExpiry = 0;
    let state = 'ACTIVE';

    switch(giftCardNumber) {
      case '123123123123':
        // Valid and not expired
        mockBalance = (Math.random() * 500 + 50).toFixed(2);
        daysUntilExpiry = Math.floor(Math.random() * 300) + 30;
        state = 'ACTIVE';
        break;
      case '321321321321':
        // Valid but expired
        mockBalance = (Math.random() * 500 + 50).toFixed(2);
        daysUntilExpiry = -15;
        state = 'EXPIRED';
        break;
      case '333333333333':
        // Valid but no balance
        mockBalance = 0;
        daysUntilExpiry = Math.floor(Math.random() * 300) + 30;
        state = 'ACTIVE';
        break;
      case '111111111111':
        // Invalid
        throw new Error('Gift card not found');
      default:
        // For any other number, simulate random valid card
        mockBalance = (Math.random() * 500 + 50).toFixed(2);
        daysUntilExpiry = Math.floor(Math.random() * 335) + 30;
        state = 'ACTIVE';
    }

    const expirationDate = new Date();
    expirationDate.setDate(expirationDate.getDate() + daysUntilExpiry);

    return Promise.resolve({
      balance: parseFloat(mockBalance),
      state: state,
      expiresOn: expirationDate.toISOString().split('T')[0],
      enabled: true  // boolean true for test data
    });
  }

  async fetchFromAPI(giftCardNumber) {
    if (!this.makeWebhookUrl) {
      throw new Error('Make.com webhook URL not configured');
    }

    const response = await fetch(this.makeWebhookUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ 
        giftCardNumber: giftCardNumber 
      })
    });

    if (!response.ok) {
      throw new Error('Network response failed');
    }

    const data = await response.json();

    if (data.error) {
      throw new Error(data.error);
    }

    // Check if gift card was found (empty fields mean not found)
    if (!data.balance || data.balance === '') {
      throw new Error('Gift card not found');
    }
    // Map Make.com fields to internal format
    // Convert string "false" and "true" to actual booleans
    const isEnabled = data.enabled === true || data.enabled === 'true';
    return {
      balance: parseFloat(data.balance || 0),
      state: 'ACTIVE',
      expiresOn: data.expires_on || null,
      enabled: isEnabled
    };
  }

  displayResult(data) {
    const balance = parseFloat(data.balance || 0);
    const state = data.state || 'UNKNOWN';
    const expiresOn = (data.expiresOn || '').trim();
    // Convert string "false" and "true" to actual booleans
    const enabled = data.enabled === true || data.enabled === 'true';
    // Compare dates reliably by converting to YYYY-MM-DD format
    const today = new Date().toISOString().split('T')[0];
    const isExpired = state === 'EXPIRED' || (expiresOn && expiresOn < today);
    const isZeroBalance = balance <= 10;
    const isInactive = state === 'DISABLED' || !enabled;

    // Format balance
    const formattedBalance = balance.toFixed(2);
    this.balanceValue.textContent = `€ ${formattedBalance}`;
    
    if (isExpired || isInactive) {
      this.balanceValue.classList.add('visually-hidden');
    } else {
      this.balanceValue.classList.remove('visually-hidden');
    }

    // Format expiration date
    if (expiresOn) {
      const expDate = new Date(expiresOn);
      const locale = document.documentElement.lang || 'en-US';
      const formattedDate = expDate.toLocaleDateString(locale, { 
        year: 'numeric', 
        month: 'long', 
        day: 'numeric' 
      });
      this.expirationValueEl.textContent = formattedDate;
    } else {
      this.expirationValueEl.textContent = '-';
    }

    // Handle different states
    if (isExpired || isInactive) {
      this.balanceStatus.textContent = this.texts.expired;
      this.balanceStatus.classList.add('expired');
      this.balanceStatus.classList.remove('active', 'zero-balance');
      this.balanceInfo.classList.add('expired');
      this.balanceInfo.classList.remove('zero-balance', 'active');
      this.balanceInfo.setAttribute('aria-label', `${this.texts.expired}. ${this.labels.expirationLabel}: ${expiresOn}`);
      this.formMessage.classList.remove('error', 'info', 'success');
      this.formMessage.classList.add('error');
      this.formMessage.textContent = this.texts.expired;
    } else if (isZeroBalance) {
      this.balanceStatus.textContent = this.texts.balanceActive;
      this.balanceStatus.classList.add('zero-balance');
      this.balanceStatus.classList.remove('expired', 'active');
      this.balanceInfo.classList.remove('expired');
      this.balanceInfo.classList.add('zero-balance');
      this.balanceInfo.setAttribute('aria-label', `${this.labels.currentBalanceLabel}: €${formattedBalance}. ${this.labels.expirationLabel}: ${expiresOn}`);
      this.formMessage.classList.remove('error', 'info');
      this.formMessage.classList.add('success');
      this.formMessage.textContent = this.texts.balanceSuccess;
    } else {
      this.balanceStatus.textContent = this.texts.balanceActive;
      this.balanceStatus.classList.add('active');
      this.balanceStatus.classList.remove('expired', 'zero-balance');
      this.balanceInfo.classList.remove('expired', 'zero-balance');
      this.balanceInfo.setAttribute('aria-label', `${this.labels.currentBalanceLabel}: €${formattedBalance}. ${this.labels.expirationLabel}: ${expiresOn}`);
      this.formMessage.classList.remove('error', 'info');
      this.formMessage.classList.add('success');
      this.formMessage.textContent = this.texts.balanceSuccess;
    }

    this.balanceInfo.style.display = 'block';
    
    // Only show form-message for errors, not for success
    if (isExpired || isInactive) {
      this.formMessage.style.display = 'block';
    }
  }

  displayError(message) {
    this.formMessage.classList.remove('success', 'info');
    this.formMessage.classList.add('error');
    this.formMessage.textContent = message || this.texts.balanceError;
    this.formMessage.style.display = 'block';
    this.balanceInfo.style.display = 'none';
  }

  async handleSubmit(e) {
    e.preventDefault();

    if (!this.validateForm()) {
      return;
    }

    // Clear previous results immediately
    this.clearResults();

    // Disable submit button
    this.submitBtn.disabled = true;
    this.submitBtn.classList.add('loading');
    this.submitBtn.setAttribute('aria-busy', 'true');
    const btnText = this.submitBtn.querySelector('.js-btn-text');
    const originalText = btnText.textContent;
    btnText.textContent = this.texts.checking;

    try {
      const giftCardNumber = this.input.value.trim();
      const data = await this.fetchGiftCardData(giftCardNumber);
      this.displayResult(data);
    } catch (error) {
      this.displayError(this.texts.balanceError);
    } finally {
      this.submitBtn.classList.remove('loading');
      this.submitBtn.setAttribute('aria-busy', 'false');
      btnText.textContent = originalText;
    }
  }
}

// Initialize when DOM is ready
document.addEventListener('DOMContentLoaded', function() {
  // Initialize all modal instances
  document.querySelectorAll('[data-section-type="modal_gift_card"]').forEach((section) => {
    const sectionId = section.getAttribute('data-section-id');
    const textStringsContainer = section.querySelector('.js-text-strings');

    if (textStringsContainer) {
      const config = {
        sectionId: sectionId,
        testMode: section.hasAttribute('data-test-mode') ? section.getAttribute('data-test-mode') === 'true' : false,
        makeWebhookUrl: section.getAttribute('data-make-webhook-url') || '',
        texts: {
          numberError: textStringsContainer.querySelector('.js-text-number-error').textContent,
          lengthError: textStringsContainer.querySelector('.js-text-length-error').textContent,
          checking: textStringsContainer.querySelector('.js-text-checking').textContent,
          check: textStringsContainer.querySelector('.js-text-check').textContent,
          balanceActive: textStringsContainer.querySelector('.js-text-balance-active').textContent,
          balanceSuccess: textStringsContainer.querySelector('.js-text-balance-success').textContent,
          balanceError: textStringsContainer.querySelector('.js-text-balance-error').textContent,
          expired: textStringsContainer.querySelector('.js-text-expired').textContent
        },
        labels: {
          currentBalanceLabel: textStringsContainer.querySelector('.js-text-current-balance-label').textContent,
          expirationLabel: textStringsContainer.querySelector('.js-text-expiration-label').textContent
        }
      };

      new ModalGiftCard(config);
    }
  });
});
